import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { slotsAPI, domainRequestsAPI } from '@hpanel/web-common/api/client'
import type { DomainRequest, Slot } from '@hpanel/web-common/api/types'
import { ConfirmModal, ErrorBox, toMessage } from '../../components/sites/siteUi'

/**
 * Tenant Sites page: each site is one container serving a primary domain, plus
 * parked domains and subdomains. Also the tenant's open requests: pending ones
 * (with Cancel) and rejected ones (with the administrator's reason).
 *
 * A primary domain cannot be removed here (only an administrator can); parked
 * names and subdomains can, each behind a Yes/No. Anything with fields is its
 * own page (request a site, park a domain, add a subdomain; CLAUDE.md §15).
 */

const REQUEST_KIND: Record<string, string> = { slot: 'New site', parked: 'Parked domain' }

interface Pending {
  id: number
  name: string
}

export const Sites: React.FC = () => {
  const location = useLocation()
  const [slots, setSlots] = useState<Slot[]>([])
  const [requests, setRequests] = useState<DomainRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(
    (location.state as { flash?: string } | null)?.flash ?? null,
  )
  const [pending, setPending] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    const [s, r] = await Promise.allSettled([slotsAPI.list(), domainRequestsAPI.list()])
    let message: string | null = null
    if (s.status === 'fulfilled') setSlots(s.value.slots)
    else message = toMessage('list sites', s.reason, 'Failed to load your sites')
    if (r.status === 'fulfilled') setRequests(r.value.requests)
    else {
      const m = toMessage('list requests', r.reason, 'Failed to load your requests')
      message = message ?? m
    }
    setError(message)
    setLoading(false)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const removeName = async () => {
    if (!pending) return
    setBusy(true)
    setError(null)
    setFlash(null)
    try {
      const r = await slotsAPI.removeName(pending.id)
      setFlash(`Removed ${r.removed.join(', ')}`)
      setPending(null)
      await refresh()
    } catch (e: unknown) {
      setPending(null)
      setError(toMessage('remove name', e, 'Failed to remove the name'))
    } finally {
      setBusy(false)
    }
  }

  const cancelRequest = async (req: DomainRequest) => {
    setError(null)
    setFlash(null)
    try {
      await domainRequestsAPI.cancel(req.id)
      setFlash(`Cancelled the request for ${req.name}`)
      await refresh()
    } catch (e: unknown) {
      setError(toMessage('cancel request', e, 'Failed to cancel the request'))
    }
  }

  const removeButton = (n: { id: number; name: string }) => (
    <button
      type="button"
      data-testid={`site-remove-${n.id}`}
      onClick={() => setPending({ id: n.id, name: n.name })}
      className="text-red-600 hover:underline text-sm"
    >
      Remove
    </button>
  )

  // Approved requests are the sites above; a cancelled one is the tenant's own doing.
  const openRequests = requests.filter((r) => r.status === 'pending' || r.status === 'rejected')

  return (
    <div data-testid="sites-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 data-testid="page-title" className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Sites
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Each site is one container serving a primary domain, plus parked domains and subdomains.
          </p>
        </div>
        <Link to="/client/sites/request" data-testid="sites-request-link" className="btn-primary self-start sm:self-auto">
          + Request a site
        </Link>
      </div>

      {flash && (
        <div
          role="status"
          data-testid="sites-flash"
          className="px-3 py-2 text-sm rounded bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200"
        >
          {flash}
        </div>
      )}
      <ErrorBox message={error} testId="sites-error" />

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      ) : slots.length === 0 && !error ? (
        <p data-testid="sites-empty" className="text-gray-600 dark:text-gray-400">
          You have no sites yet. Click <strong>+ Request a site</strong> above; an administrator reviews the
          request.
        </p>
      ) : (
        <div className="space-y-4">
          {slots.map((slot) => (
            <section
              key={slot.container_id}
              data-testid={`site-${slot.container_id}`}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    <span data-testid={`site-primary-${slot.container_id}`}>{slot.primary.name}</span>
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-mono">{slot.image}</span>
                    {' · '}
                    <span data-testid={`site-status-${slot.container_id}`}>{slot.status}</span>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <Link
                    to={`/client/sites/${slot.container_id}/park`}
                    data-testid={`site-park-link-${slot.container_id}`}
                    className="text-primary-600 hover:underline"
                  >
                    Park a domain
                  </Link>
                  {slot.supports_subdomains && (
                    <Link
                      to={`/client/sites/${slot.container_id}/subdomains/new`}
                      data-testid={`site-subdomain-link-${slot.container_id}`}
                      className="text-primary-600 hover:underline"
                    >
                      Add subdomain
                    </Link>
                  )}
                </div>
              </div>

              {!slot.supports_subdomains && (
                <p
                  data-testid={`site-subdomains-unavailable-${slot.container_id}`}
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {slot.needs_recreate
                    ? 'Subdomains are not available on this site yet: it needs to be rebuilt. Ask your hosting provider.'
                    : 'Subdomains are not available on this site: its image cannot serve them. Ask your hosting provider if you need them.'}
                </p>
              )}

              {(slot.parked.length > 0 || slot.subdomains.length > 0) && (
                <ul className="text-sm divide-y divide-gray-100 dark:divide-gray-700">
                  {slot.parked.map((n) => (
                    <li
                      key={n.id}
                      data-testid={`site-parked-${n.id}`}
                      className="flex items-center justify-between py-1"
                    >
                      <span>
                        <span className="font-mono">{n.name}</span>{' '}
                        <span className="text-xs text-gray-500">parked</span>
                      </span>
                      {removeButton(n)}
                    </li>
                  ))}
                  {slot.subdomains.map((n) => (
                    <li
                      key={n.id}
                      data-testid={`site-subdomain-${n.id}`}
                      className="flex items-center justify-between py-1"
                    >
                      <span>
                        <span className="font-mono">{n.name}</span>{' '}
                        <span className="text-xs text-gray-500">subdomain</span>
                      </span>
                      {removeButton(n)}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {openRequests.length > 0 && (
        <section data-testid="sites-requests" className="space-y-2">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Requests</h2>
          <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                <tr>
                  <th className="px-4 py-3">Request</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {openRequests.map((r) => (
                  <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-3" data-testid={`request-kind-${r.id}`}>{REQUEST_KIND[r.kind] ?? r.kind}</td>
                    <td className="px-4 py-3 font-mono" data-testid={`request-name-${r.id}`}>{r.name}</td>
                    <td className="px-4 py-3" data-testid={`request-status-${r.id}`}>{r.status}</td>
                    <td className="px-4 py-3" data-testid={`request-reason-${r.id}`}>{r.reason ?? ''}</td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'pending' && (
                        <button
                          type="button"
                          data-testid={`request-cancel-${r.id}`}
                          onClick={() => void cancelRequest(r)}
                          className="text-red-600 hover:underline"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {pending && (
        <ConfirmModal
          testId="site-confirm"
          title={`Remove ${pending.name}?`}
          confirmLabel="Remove"
          busy={busy}
          onConfirm={() => void removeName()}
          onCancel={() => setPending(null)}
        >
          <p>
            <strong>{pending.name}</strong> stops being served. The site and its other names stay.
          </p>
        </ConfirmModal>
      )}
    </div>
  )
}
